import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['search', 'list', 'metadata', 'loading']
  static values = {
    url: String,
    currentValue: String
  }

  connect() {
    this.page = 1
    this.hasMore = true
    this.isLoading = false
    this.searchTerm = ''
    this.hasLoadedOnce = false

    // Get metadata for form building
    if (this.hasMetadataTarget) {
      this.formAction = this.metadataTarget.dataset.formAction
      this.authenticityToken = this.metadataTarget.dataset.authenticityToken
      this.turboFrame = this.metadataTarget.dataset.turboFrame
      this.field = this.metadataTarget.dataset.field
      this.paramKey = this.metadataTarget.dataset.paramKey
    }

    // Only load users when dropdown is opened
    this.element.addEventListener('shown.bs.dropdown', () => {
      if (!this.hasLoadedOnce) {
        this.loadUsers()
        this.hasLoadedOnce = true
      }
      // Focus search input when dropdown opens
      if (this.hasSearchTarget) {
        this.searchTarget.focus()
      }
    })
  }

  filter(event) {
    this.searchTerm = event.target.value.trim()
    this.page = 1
    this.hasMore = true
    // Don't clear the list immediately - let the results replace it
    this.loadUsers(true)
  }

  preventClose(event) {
    event.stopPropagation()
  }

  scroll(event) {
    const element = event.target
    const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50

    if (isNearBottom && this.hasMore && !this.isLoading) {
      this.page++
      this.loadUsers()
    }
  }

  async loadUsers(isSearch = false) {
    if (this.isLoading) return

    this.isLoading = true
    // Only show loading indicator on initial load and pagination, not during search
    if (!isSearch) {
      this.showLoading()
    }

    try {
      // If urlValue is already absolute, use it directly; otherwise make it relative to origin
      const baseUrl = this.urlValue.startsWith('http') ? this.urlValue : new URL(this.urlValue, window.location.origin).toString()
      const url = new URL(baseUrl)
      url.searchParams.set('paginate', 'true')
      url.searchParams.set('page', this.page)
      if (this.currentValueValue) {
        url.searchParams.set('current_value', this.currentValueValue)
      }
      if (this.searchTerm) {
        url.searchParams.set('q', this.searchTerm)
      }

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      this.hasMore = data.pagination?.more || false
      // Clear list if it's a search or initial load (page 1), otherwise append for pagination
      if (isSearch || this.page === 1) {
        this.clearList()
      }
      this.renderUsers(data.results)
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      this.isLoading = false
      this.hideLoading()
    }
  }

  renderUsers(users) {
    users.forEach(user => {
      const li = document.createElement('li')
      li.className = 'list-group-item list-group-item-action p-0'
      li.dataset.searchable = user.text.toLowerCase()

      // Check if this user is currently selected
      const isActive = user.is_selected || user.id.toString() === this.currentValueValue?.toString()

      // If user is selected, clicking should unassign (set to nil)
      const valueToSubmit = isActive ? '' : user.id

      // Build the form HTML
      li.innerHTML = `
        <form action="${this.formAction}" accept-charset="UTF-8" method="post" data-turbo="true" data-turbo-frame="${this.turboFrame}" class="d-block">
          <input type="hidden" name="_method" value="patch" autocomplete="off">
          <input type="hidden" name="authenticity_token" value="${this.authenticityToken}" autocomplete="off">
          <input type="hidden" name="${this.paramKey}[${this.field}]" value="${valueToSubmit}" autocomplete="off">
          <button type="submit" class="btn btn-link w-100 text-start py-2 text-decoration-none ${isActive ? 'bg-light' : ''}" style="color: inherit; border: none;">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center gap-2">
                <div class="d-flex flex-column">
                  <span class="text-dark">${user.text}</span>
                  ${user.email ? `<span class="text-muted small">${user.email}</span>` : ''}
                </div>
              </div>
              ${isActive ? '<i class="fas fa-check-circle text-primary"></i>' : ''}
            </div>
          </button>
        </form>
      `

      this.listTarget.appendChild(li)
    })
  }

  clearList() {
    // Remove all user list items (keep the search and clear option)
    const userItems = this.listTarget.querySelectorAll('li:not([data-keep])')
    userItems.forEach(item => item.remove())
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('d-none')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('d-none')
    }
  }
}
